'use client';

import { c as _c } from "react/compiler-runtime";
import { Pause, Play } from '@phosphor-icons/react';
import Image from 'next/image';
import { useCallback, useRef, useState } from 'react';
import { SearchField } from './search-field';

/**
 * Compression commands (two-pass, bitrate chosen to target ~4MB: 4MB * 8 / duration in seconds):

mp4:

ffmpeg -y -i input.mov \
  -map 0:v:0 \
  -vf "scale=1920:-2:flags=lanczos" \
  -c:v libx264 \
  -b:v 1750K \
  -preset veryslow \
  -pix_fmt yuv420p \
  -profile:v main \
  -level 4.0 \
  -an \
  -pass 1 \
  -f null /dev/null

ffmpeg -i input.mov \
  -map 0:v:0 \
  -vf "scale=1920:-2:flags=lanczos" \
  -c:v libx264 \
  -b:v 1750K \
  -preset veryslow \
  -pix_fmt yuv420p \
  -profile:v main \
  -level 4.0 \
  -movflags +faststart \
  -write_tmcd 0 \
  -an \
  -pass 2 \
  output.mp4

webm:

ffmpeg -y -i input.mov \
  -map 0:v:0 \
  -vf "scale=1920:-2:flags=lanczos" \
  -c:v libvpx-vp9 \
  -b:v 1750K \
  -deadline good \
  -cpu-used 4 \
  -row-mt 1 \
  -pix_fmt yuv420p \
  -an \
  -pass 1 \
  -f null /dev/null

ffmpeg -i input.mov \
  -map 0:v:0 \
  -vf "scale=1920:-2:flags=lanczos" \
  -c:v libvpx-vp9 \
  -b:v 1750K \
  -deadline good \
  -cpu-used 0 \
  -row-mt 1 \
  -pix_fmt yuv420p \
  -an \
  -pass 2 \
  output.webm

stabilization (handheld footage, "strong-2": vidstab, 13% crop-in to hide the moving edges,
trimmed to 23s via -t 23):

ffmpeg -i input.mp4 \
  -vf "vidstabdetect=shakiness=8:accuracy=15:stepsize=6:result=transforms.trf" \
  -f null -

ffmpeg -y -i input.mp4 \
  -vf "vidstabtransform=input=transforms.trf:smoothing=60:zoom=13:optzoom=0:crop=keep:interpol=bicubic:maxangle=0.05,unsharp=5:5:0.6:3:3:0.0,format=yuv420p" \
  -t 23 \
  -c:v libx264 \
  -crf 23 \
  -maxrate 1.6M \
  -bufsize 3.2M \
  -preset slow \
  -profile:v main \
  -movflags +faststart \
  -an \
  output.mp4

ffmpeg -y -i input.mp4 \
  -vf "<same vidstabtransform chain as above>" \
  -t 23 \
  -c:v libvpx-vp9 \
  -b:v 1.3M \
  -deadline good \
  -cpu-used 2 \
  -row-mt 1 \
  -an \
  -pass 1 \
  -f null /dev/null

ffmpeg -y -i input.mp4 \
  -vf "<same vidstabtransform chain as above>" \
  -t 23 \
  -c:v libvpx-vp9 \
  -b:v 1.3M \
  -deadline good \
  -cpu-used 2 \
  -row-mt 1 \
  -an \
  -pass 2 \
  output.webm

fallback poster image:

ffmpeg -i input.mov \
  -map 0:v:0 \
  -vf "scale=1920:-2:flags=lanczos" \
  -frames:v 1 \
  -q:v 20 \
  fallback.jpg
 */

export function HeroVideo() {
  const $ = _c(10);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const togglePlayback = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image src="/images/autumn-fallback.jpg" className="absolute inset-0 object-cover brightness-[0.85]" alt="" fill={true} priority={true} sizes="100vw" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <video ref={videoRef} autoPlay={true} loop={true} muted={true} playsInline={true} poster="/images/autumn-fallback.jpg" id="hero-video" className="absolute inset-0 size-full object-cover brightness-[0.85] motion-reduce:hidden"><source src="/videos/autumn.webm" type="video/webm; codecs=vp9" /><source src="/videos/autumn.mp4" type="video/mp4" /></video>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h1 className="flex flex-col gap-2 text-white"><span className="animate-fade-up font-display motion-reduce:animate-none">{"Mach jeden Tag "}<br />zum Freitag!</span><span className="animation-delay-200 animate-fade-up font-body-copy motion-reduce:animate-none">Unterkünfte, Erlebnisse und mehr – einfach buchen, sofort erleben.</span></h1>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="fluid-grid fluid-container sm:sticky-top-offset-16 absolute inset-0 place-content-center sm:place-content-start"><div className="col-span-6 flex flex-col gap-12 sm:col-span-12 sm:col-start-3 sm:col-end-11 sm:gap-14">{t3}<div className="animation-delay-400 z-10 animate-fade-up motion-reduce:animate-none"><SearchField /></div></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isPlaying ? "Video pausieren" : "Video abspielen";
  let t6;
  if ($[5] !== isPlaying) {
    t6 = isPlaying ? <Pause className="size-4" weight="fill" /> : <Play className="size-4" weight="fill" />;
    $[5] = isPlaying;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = <>{t1}{t2}{t4}<button type="button" onClick={togglePlayback} aria-label={t5} className="absolute right-4 bottom-12 z-10 inline-grid size-8 cursor-pointer place-content-center rounded-lg bg-white/75 text-primary motion-reduce:hidden">{t6}</button></>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}